import React from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EarthquakeIcon } from "../../../../assets/disaster-icons/earthquake.svg";
import { ReactComponent as FireIcon } from "../../../../assets/disaster-icons/fire.svg";
import { ReactComponent as TornadoIcon } from "../../../../assets/disaster-icons/tornado.svg";
import { ReactComponent as HurricaneIcon } from "../../../../assets/disaster-icons/hurricane.svg";
import { ReactComponent as FloodIcon } from "../../../../assets/disaster-icons/flood.svg";
import { ReactComponent as VolcanoIcon } from "../../../../assets/disaster-icons/volcano.svg";
import { ReactComponent as FederalIcon } from "../../../../assets/disaster-icons/federal.svg";
import { ReactComponent as StateIcon } from "../../../../assets/disaster-icons/state.svg";
import { ReactComponent as LocalIcon } from "../../../../assets/disaster-icons/local.svg";
import { ReactComponent as ViewEye } from "../../../../assets/view-eye.svg";

const DisasterCard = ({ disaster, statusColors, disasterStageStyles }) => {
  const navigate = useNavigate();

  // Add this color mapping object inside the DisasterCard component
  const disasterTypeStyles = {
    Fire: { color: "#780404", background: "#F00", icon: <FireIcon /> },
    Flood: { color: "#15659B", background: "#2788C9", icon: <FloodIcon /> },
    Tornado: {
      color: "#454545",
      background: "#454545",
      icon: <TornadoIcon />,
    },
    Hurricane: {
      color: "#6B0E93",
      background: "#6B0E93",
      icon: <HurricaneIcon />,
    },
    Earthquake: {
      color: "#F56D13",
      background: "#D29000 ",
      icon: <EarthquakeIcon />,
    },
    Volcano: {
      color: "#945E14",
      background: "#945E14",
      icon: <VolcanoIcon />,
    },
  };

  const disasterImpactStyles = {
    Minor: { color: "#155724", background: "#0A8525" },
    Moderate: { color: "#856404", background: "#E4C305" },
    Major: { color: "#721c24", background: "#FF8800" },
    Catastrophic: { color: "#ffffff", background: "#CE2222" },
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  const handleNavigate = () => {
    navigate(`/disasterdeclaration/list`, {
      state: { selectedDisasterId: disaster.disaster_id },
    });
  };

  return (
    <div className="disaster-card flex h-full flex-col rounded-lg bg-white p-3 pb-0 shadow-sm">
      <div className="flex flex-grow flex-col gap-2">
        {/* Disaster Name and Status */}
        <div className="flex justify-between">
          {/* Left Div (80% Width) */}
          <div className="w-4/5">
            <div className="flex items-center">
              <span
                className="disaster-type rounded-full px-1 text-sm"
                style={{
                  backgroundColor:
                    disasterTypeStyles[disaster.disaster_type]?.background,
                  color: "#FFF",
                }}
                title={disaster.disaster_type}
              >
                <span className="disaster-type-icon">
                  {disasterTypeStyles[disaster.disaster_type]?.icon}
                </span>
              </span>
              <span
                className="disaster-card-title flex items-center"
                style={{
                  color:
                    disasterTypeStyles[disaster.disaster_type]?.background,
                }}
              >
                <span className="ml-1">{disaster.disaster_name}</span>
              </span>
            </div>
            <p className="mt-2 text-sm">
              <span className="text-gray-500">Declared on </span>
              <span className="text-gray-900">{disaster.date_declared}</span>
            </p>
          </div>

          {/* Right Div (20% Width) */}
          <div className="flex w-1/5 justify-end gap-2">
            {/* Ensure disaster_level is an array before calling .includes() */}
            {Array.isArray(disaster.disaster_level) &&
              disaster.disaster_level.includes("Federal") && (
                <FederalIcon className="h-8 w-8 rounded border border-solid p-1" />
              )}

            {Array.isArray(disaster.disaster_level) &&
              disaster.disaster_level.includes("State" || "States") && (
                <StateIcon className="h-8 w-8 rounded border border-solid p-1" />
              )}

            {Array.isArray(disaster.disaster_level) &&
              disaster.disaster_level.includes("Local") && (
                <LocalIcon className="h-8 w-8 rounded border border-solid p-1" />
              )}
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-700 text-sm">
          {truncateText(disaster.description, 250)}
        </p>

        {/* Type Badge and View Details - Now in a separate div with mt-auto */}
        <div className="mt-auto flex items-center justify-between">
          {/* Left Section (70% Width) */}
          <div className="w-7/10 flex items-center">
            <span
              className="disaster-type rounded-full px-1 text-xs text-white"
              style={{
                backgroundColor: statusColors[disaster.disaster_status],
              }}
            >
              {disaster.disaster_status === "active" && "Active •"}
              {disaster.disaster_status === "inactive" && "Inactive •"}
              {disaster.disaster_status === "closed" && "Closed •"}
              {disaster.disaster_status === "declared" && "Declared •"}
              {disaster.disaster_status === "approved" && "Approved •"}
            </span>

            {/* Wrapped disaster_stage in a styled span */}
            <span
              className="disaster-type ml-2 rounded-full px-1 text-xs"
              style={{
                backgroundColor:
                  disasterStageStyles[disaster.disaster_stage]?.background,
                color: disasterStageStyles[disaster.disaster_stage]?.color,
              }}
              title={disaster.disaster_stage}
            >
              {disaster.disaster_stage}
            </span>

            {disaster.disaster_impact && (
              <span
                className="ml-2 rounded-full px-2 py-1 text-xs text-white"
                style={{
                  backgroundColor:
                    disasterImpactStyles[disaster.disaster_impact]
                      ?.background,
                }}
                title={`Impact Level: ${disaster.disaster_impact}`}
              >
                {disaster.disaster_impact}
              </span>
            )}
          </div>

          {/* Right Section (30% Width) */}
          <div className="w-3/10 flex justify-end">
            <button
              onClick={handleNavigate}
              className="view-details-btn flex items-center bg-white px-3 py-3 text-blue-800 hover:cursor-pointer"
            >
              <ViewEye className="mr-2 inline-block" />
              View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisasterCard;
